import React, { createContext, Dispatch, memo, Reducer, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NewInput, NewSelect, Overlay, TooltipString, WaitAnimation, ANIMAL } from "..";
import { colors, fonts, glyphs, frames, css } from "../../../Styles";
import { ButtonTypes, NewFieldStyles, NewFieldTypes } from "../../../Definitions";
import { AnimalInfo, Graph_QL, Location, MaintainAnimal, useAnimal, useSkipFeedTime } from "../../../Services";
import { useAnimalOptions } from "../../_Components";
import NewButton, { NewButtonList } from "../newButton";
import { EsfLocation } from "../../../Services/Agrisys_GqlDataCaller/WalkThroughDataCalls/getEsfStructureCall";

// #region | Pen Context and Reducer
/* eslint-disable */
enum IPenContextEnum {
  currentViewType = "currentViewType",
  tempData = "tempData",
  replaceTempData = "replaceTempData",
  error = "error",
}

export enum currentViewTypes {
  basic = "basic",
  edit = "edit",
  add = "add",
  move = "move"
}

export enum ListTypes {
  animals = "animals",
  pens = "pens"
}
/* eslint-enable */

interface ChangedAnimalData { 
  penId: number, animalId: number,
  data: Omit<MaintainAnimal, "animal_id">,
}

type IPenContext = {
  farm: string;
  domain: string;
  connection: Graph_QL.Connection;
  currentViewType: currentViewTypes;
  pens: Location[] | EsfLocation[];
  tempData: ChangedAnimalData[];
  error: {Elementid:number, errorText:string};
};

type PenDispatcherT = Deep_Partial<IPenContext> & {replaceTempData?: ChangedAnimalData[]};
type PenContexter = {
  state: IPenContext,
  dispatch: Dispatch<PenDispatcherT>
}

export const PenContext = createContext<PenContexter | undefined>(undefined);
function PenReducer(state: IPenContext, action: PenDispatcherT) {
  let ResultState = state;
  Object.entries(action).forEach(([type, payload], index) => {
    switch (type) {
      case IPenContextEnum.currentViewType:
        ResultState = Object.assign({}, ResultState, {
          currentViewType: payload
        } as PenDispatcherT)
        break;
      case IPenContextEnum.tempData:
        let changedState = ResultState.tempData;
        (payload as ChangedAnimalData[]).forEach(newData => {
          let animalChangeIndex = changedState.findIndex(e => e.penId === newData.penId && e.animalId === newData.animalId)
          if (animalChangeIndex >= 0) {
            changedState[animalChangeIndex].data = Object.assign({}, changedState[animalChangeIndex].data, newData.data)
          }
          else changedState.push(newData);
        })
        ResultState = Object.assign({}, ResultState, {
          tempData: [...changedState] 
        } as PenDispatcherT)
        break;
      case IPenContextEnum.replaceTempData:
        ResultState = Object.assign({}, ResultState, {tempData: payload})
      break
      case IPenContextEnum.error:
        ResultState = Object.assign({}, ResultState, {
          error: payload
        } as PenDispatcherT)
        break;
      default:
        console.error(`Unhandled action type: ${type}`)
        break
    }
  })
  return ResultState;
}

export const usePenContext = () => {
  const context = useContext(PenContext);
  if (!context) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
};
// #endregion

// #region | ButtonList Contents and Styles
const getButtonStyles = (index: number, view: currentViewTypes, listType: ListTypes) => {
  if (view === currentViewTypes.add && index === 2) {
    return { backgroundColor: colors.greyLightSecondary, opacity: 1 };
  }
  if (view === currentViewTypes.edit && listType === ListTypes.animals  && index === 2) {
    return { backgroundColor: colors.greyLightSecondary, opacity: 1 };
  }
  if (view === currentViewTypes.edit && listType !== ListTypes.animals && index === 4) {
    return { backgroundColor: colors.greyLightSecondary, opacity: 1 };
  }
  if (view === currentViewTypes.move && index === 3) {
    return { backgroundColor: colors.greyLightSecondary, opacity: 1 };
  }
  return { backgroundColor: colors.greyLightPrimary, opacity: 0.5 };
};

const useContents = (
  func_add: () => void,
  func_move: () => void,
  func_edit: () => void,
  func_cancel: () => void,
  func_save: () => void,
  currentViewType: currentViewTypes,
  listType: ListTypes
): [string[], JSX.Element[], (() => void)[]] => {
  const { t } = useTranslation();
  const buttonEntries: { label: string; glyphs: JSX.Element; onClick: () => void; }[] = [];
  
  if (currentViewType !== currentViewTypes.basic) {
    buttonEntries.push(
      { label: t("buttons.save", {defaultValue: "save"}), glyphs: glyphs.Check({ fillColor: colors.greyDarkPrimary }), onClick: func_save },
      { label: t("buttons.cancel", {defaultValue: "cancel"}), glyphs: glyphs.Close({ fillColor: colors.greyDarkPrimary }), onClick: func_cancel }
    );
  }

  if (listType === ListTypes.pens) {
    buttonEntries.push(
      { label: t("buttons.add", {defaultValue: "add"}), glyphs: glyphs.Add({ fillColor: colors.greyDarkPrimary }), onClick: func_add },
      { label: t("buttons.move", {defaultValue: "move"}), glyphs: glyphs.AnimalMove({ fillColor: colors.greyDarkPrimary }), onClick: func_move }
    )
  }
    
  buttonEntries.push(
    { label: t("buttons.edit", {defaultValue: "edit"}), glyphs: glyphs.Edit({ fillColor: colors.greyDarkPrimary }), onClick: func_edit }
  );

  const labels = buttonEntries.map((b) => b.label);
  const icons = buttonEntries.map((b) => b.glyphs)
  const onclicks = buttonEntries.map((b) => b.onClick);
  return [labels, icons, onclicks];
};
// #endregion

// #region | Level Disclaimer Contents
const useLevelDisclaimer = (view: currentViewTypes) => {
  const { t } = useTranslation();
  if (view === currentViewTypes.add) {
    return t("penList.addDisclaimer", {defaultValue:"Add an animal from the dropdown or remove it again on the bin icon"})
  }
  if (view === currentViewTypes.edit) {
    return t("penList.editDisclaimer", {defaultValue:"Edit an animal by selecting new values"})
  }
  if (view === currentViewTypes.move) {
    return t("penList.moveDisclaimer", {defaultValue:"Move an animal to a new location from the dropdown or remove it from any location on the bin icon"})
  }
  return t("penList.basicDisclaimer", {defaultValue:"Hold the mouse on a value to see column type"})
}
// #endregion

// #region | Pen Limit Amount of Animals
const getPenLimitFromSystemType = (systemType: number) => {
  let penLimit: number | undefined;
        
  if (systemType === 1) {
    penLimit = undefined;
  } else if (systemType === 2 || systemType === 3) {
    penLimit = undefined;
  } else {
    penLimit = undefined; 
  }
  return {
    penLimit: penLimit
  };
}
// #endregion
// #region | Headers
const Header = memo(({ currentViewType }: { currentViewType: currentViewTypes }) => {
  const { t } = useTranslation();
  switch (currentViewType) {
    case currentViewTypes.basic:
    case currentViewTypes.edit:
      const isEdit = currentViewType === currentViewTypes.edit
      return (
        <div style={{ backgroundColor: colors.greyLightSecondary, width: "695px", height: "35px", borderRadius: "10px 10px 0px 0px", display: "grid", gridTemplateColumns: "55px 109px 45px 108px 45px 108px 45px 90px 50px", gap: "5px", padding: "0px 20px", alignItems: "center" }}>
          <TooltipString contentString={t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} duplicatedStrings/>
          { isEdit ?  <TooltipString contentString={t("locationFeederPages.locationFeeder.feedSkip", { defaultValue: "Feed Skip" })} duplicatedStrings overrides={{contentstring: {paddingLeft: "7px"}}}/> : <span></span>}
          <TooltipString contentString={t("locationFeederPages.locationFeeder.animalId", { defaultValue: "Animal No." })} duplicatedStrings/>
          <TooltipString contentString={t("milestones.general.latestMilestone", { defaultValue: "Curr. milestone" })} duplicatedStrings/>
          <TooltipString contentString={t("milestones.general.milestoneDayNow", { defaultValue: "Curr. milestone day" })} duplicatedStrings/>
          <TooltipString contentString={t("feed.feedCurve.feedCurve", { defaultValue: "Feed Curve" })} duplicatedStrings overrides={{contentstring: {paddingLeft: "7px"}}}/>
          <TooltipString contentString={t("feed.feedCorrection.feedCorrection", { defaultValue: "Feed Corrections" })} duplicatedStrings overrides={{contentstring: {paddingLeft: "5px"}}}/>
          <TooltipString contentString={`${t("feed.general.remainingToday", { defaultValue: "Remaining Today" })} (${t("units.kg", { defaultValue: "KG" })})`} duplicatedStrings/>
          <TooltipString contentString={`${t("feed.general.extra", { defaultValue: "Extra Fed Today" })} (${t("units.kg", { defaultValue: "KG" })})`} duplicatedStrings/>
        </div>
      );
    case currentViewTypes.move:
      return (
        <div style={{ backgroundColor: colors.greyLightSecondary, width: "695px", height: "35px", borderRadius: "10px 10px 0px 0px", display: "grid", gridTemplateColumns: "55px 109px 45px 446px", gap: "5px", padding: "0px 20px", alignItems: "center" }}>
          <TooltipString contentString={t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} duplicatedStrings/>
          <span></span>
          <TooltipString contentString={t("locationFeederPages.locationFeeder.animalId", { defaultValue: "Animal No." })} duplicatedStrings/>
          <TooltipString contentString={t("milestones.general.location", { defaultValue: "Location" })} duplicatedStrings/>
        </div>
      );
    case currentViewTypes.add:
      return (
        <div style={{ backgroundColor: colors.greyLightSecondary, width: "695px", height: "35px", borderRadius: "10px 10px 0px 0px", display: "grid", gridTemplateColumns: "55px 109px 491px", gap: "5px", padding: "0px 20px", alignItems: "center" }}>
          <TooltipString contentString={t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} duplicatedStrings/>
          <span></span>
          <TooltipString contentString={t("params.animal", { defaultValue: "Animal" })} duplicatedStrings/>
        </div>
      );
    default:
      return <>Empty STATE ERROR</>
  }
})
// #endregion

// #region | Empty Content
const EmptyContent = memo(({ pen, currentViewType, error, handleAddAnimal }: {
  pen: Location;
  currentViewType: currentViewTypes;
  error?: { Elementid: number; errorText: string };
  handleAddAnimal?: (selectedAnimalId: number, selectedAnimalNumber: number, pen: Location) => void;
}) => {
  const { t } = useTranslation();
  const animalOptions = useAnimalOptions();

    switch (currentViewType) {
      case currentViewTypes.basic:
      case currentViewTypes.edit:
        return (
          <p style={{ ...css.emptyPen, opacity: "0.5" }}>
            {t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty Pen"})}
          </p>
        );

      case currentViewTypes.move:
        return null;

      case currentViewTypes.add:
        const isCurrentPenError = error && error.Elementid === pen.location_id && error.errorText !== "";

        return (
          <div style={{ display: "grid", gridTemplateColumns: "20px 55px 108px 278px", height: "35px", alignItems: "center", gap: "5px", cursor: "default", zIndex: 999 }}>
            <glyphs.Arrow rotate={180} fillColor={colors.greyDarkPrimary} />
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}> {t("params.animal", { defaultValue: "Animal" })} </p>
            <div style={{ width: "108px" }}>
              <NewSelect
                onChange={(selectedValue) => handleAddAnimal?.(Number(selectedValue?.id), Number(selectedValue?.label), pen)}
                options={animalOptions ?? []}
                fieldOverrides={{
                  fieldStyle: NewFieldStyles.greyTertiary,
                  fieldType: NewFieldTypes.tertiaryField,
                  fieldWidth: "100%",
                  menuWidth: "280px",
                }}
              />
            </div>
            {isCurrentPenError ? (
              <p key={pen.location_id} style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.systemRed }} >
                {error.errorText}
              </p>
            ) : null}
          </div>
        );

      default: return <>Empty STATE ERROR</>;
    }
  }
);
// #endregion 

interface PenList_Props {
  // systemId: number;
  listType: ListTypes;
  systemType: number;
  pens: Location[] | EsfLocation[];
  section?: number;
  headerPen?: boolean
  onPenClick?: (penId: number) => void;
  onAnimalClick?: (animalId: number) => void;
  connect: {
    farm: string
    domain: string
    connection: Graph_QL.Connection
  }
}

export default function PenList({ listType, systemType, pens, section, headerPen, onPenClick, onAnimalClick, connect }: PenList_Props) {
  const { t } = useTranslation();
  const { maintainEntries: updateAnimals } = useAnimal(connect.connection);
  const [loading, setLoading] = useState(false);

  // Reducer
  const [_state, dispatch] = useReducer<Reducer<IPenContext, PenDispatcherT>>(PenReducer, {
    farm: connect.farm,
    domain: connect.domain,
    connection: connect.connection,
    currentViewType: currentViewTypes.basic,
    pens: pens,
    tempData: [],
    systemType: systemType,
    error: { Elementid: 0, errorText: "" }
  } as IPenContext);

  // Feed Skip
  const [feedSkipTime, setFeedSkipTime] = useState<Map<number, number>>(new Map());
  const feedSkipSuccess = useRef<{[locationId: number]: boolean}>({})
  const { skipFeedTimeLocations} = useSkipFeedTime();
  const [missUseOfMemoFixer, setMissUseOfMemoFixer] = useState(0);

  useEffect(() => {
    _state.pens = pens;
    setMissUseOfMemoFixer((old) => old+1);
  }, [pens]);

  // #region | Handle Functions
  // Handle save
  const handleSave = () => {
    switch (_state.currentViewType) {
      // Edit animal
      case currentViewTypes.edit:
        let UpdateEdit = _state.tempData.map(e => {if (e.data.milestone_day) {return { animal_id: e.animalId, ...e.data, milestone_day: Math.floor(e.data.milestone_day / 1000)}} return { animal_id: e.animalId, ...e.data }})
        
        if (updateAnimals) updateAnimals({ data: UpdateEdit }, {}, {}, (updateResponse: AnimalInfo[]) => {
          const newData = updateResponse.map((animal) => ({...animal, correction: animal.correction ?  animal.correction-100 : undefined}));
          _state.pens = _state.pens.map(pen => {
            if (pen.animals.some(e => newData.find(u => u.animal_id === e.animal_id))) {
              const newPen = {
                ...pen,
                animals: pen.animals.map(animalInPen => {
                  const updatedAnimal = newData.find(u => u.animal_id === animalInPen.animal_id);
                  return updatedAnimal ? updatedAnimal : animalInPen;
                })
              };
              return newPen;
            }
            return pen;
          });
          _state.tempData = [];
          dispatch({ currentViewType: currentViewTypes.basic });
        });
        break;
      
      // Add animal
      case currentViewTypes.add:
        let UpdateAdd = _state.tempData.map(e => ({ animal_id: e.animalId, ...e.data }))
        if (updateAnimals) updateAnimals({ data: UpdateAdd }, {}, {}, (updateResponse: AnimalInfo[]) => {
          const newData = updateResponse.map((animal) => ({...animal, correction: animal.correction ?  animal.correction-100 : undefined}));
          _state.pens = _state.pens.map(pen => {
            if (newData.find(u => u.location_id === pen.location_id)) {
              const newPen = { ...pen, }
              newPen.animals = newPen.animals.concat(newData.filter(e => e.location_id === newPen.location_id))
              return newPen;
            }
            return pen;
          });
          _state.tempData = [];
          dispatch({ currentViewType: currentViewTypes.basic });
        });
        break;
      
      // Move animal
      case currentViewTypes.move:
        let UpdateMove = _state.tempData.map(e => ({ animal_id: e.animalId, ...e.data }))
        if (updateAnimals) updateAnimals({ data: UpdateMove }, {}, {}, (updateResponse: AnimalInfo[]) => {
          const newData = updateResponse.map((animal) => ({...animal, correction: animal.correction ?  animal.correction-100 : undefined}));
          _state.pens = _state.pens.map(pen => {
            const newPen = { ...pen, }
            if (newData.find(u => u.location_id === pen.location_id)) {
              newPen.animals = newPen.animals.concat(newData.filter(e => e.location_id === newPen.location_id))
            }
            return newPen;
          });
          _state.tempData = [];
          dispatch({ currentViewType: currentViewTypes.basic });
        });
        break;
      default:
        return;
    }
  };

  // Handle delete when currentViewTypes.edit
  const handleMoveDelete = (animalId: number, penId: number) => {
    dispatch({ tempData: [{ penId: penId, animalId: animalId, data: { location: Number.NaN } }] })
  };

  // Handle delete when currentViewTypes.add
  const handleAddDelete = (animalId: number, penId: number) => {
    const filtered = _state.tempData.filter(
      e => !(e.animalId === animalId && e.penId === penId)
    );
  
    dispatch({ replaceTempData: filtered });
  };

  // Handle add animal
  const handleAddAnimal = (selectedAnimalId: number, selectedAnimalNumber: number, pen: Location) => {
    const oldTemp = _state.tempData.find(e => e.animalId === selectedAnimalId);
    const alreadyInTemp = !!oldTemp;
    const isAnimalAlreadyInPen = pen.animals.some((animal => animal.animal_id === selectedAnimalId));
    dispatch({ error: { Elementid: 0, errorText: "" } });
  
    // Animal is already in tempData (has been assigned a new pen already)
    if (alreadyInTemp) {
      dispatch({ error: { Elementid: pen.location_id, errorText: oldTemp!.data.location === pen.location_id ? t("locationFeederPages.alreadyInThisPen", { defaultValue: "This animal is already in the pen!" }) : t("locationFeederPages.alreadyInAnotherPen", { defaultValue: "This animal is already added to another pen!" })}});
      setTimeout(() => {
        dispatch({ error: { Elementid: 0, errorText: "" } });
      }, 2000);

      return;
    }

    // Animal is already in pen
    if (isAnimalAlreadyInPen) {
      dispatch({ error: { Elementid: pen.location_id, errorText: t("locationFeederPages.animalAlreadyInPen", {defaultValue: "This animal is already in the pen!"}) } });      
      setTimeout(() => {
        dispatch({ error: { Elementid: 0, errorText: "" } });
      }, 2000);
      return;
    }

    // Pen limit has been reached
    const penLimitNumber = getPenLimitFromSystemType(systemType);
    if (pen.animals.length === penLimitNumber.penLimit) {
      dispatch({ error: { Elementid: pen.location_id, errorText: t("locationFeederPages.full pen", {defaultValue: "This pen is full!"}) } });      
      setTimeout(() => {
        dispatch({ error: { Elementid: 0, errorText: "" } });
      }, 2000);
      return;
    }

    dispatch({
      tempData: [{
        penId: pen.location_id, animalId: selectedAnimalId, data: {
          location: pen.location_id, animal_number: selectedAnimalNumber
        }
      } as ChangedAnimalData] 
    })
  }

  // Handle Pen Click: onClick
  const handleOnPenClick = (pen: Location, currentViewType: currentViewTypes) => {
    if (pen.animals.length === 0 || currentViewType !== currentViewTypes.basic) {
      return; 
    }
    if (onPenClick) onPenClick(pen.location_id);
  }
  // #endregion

  const handleOnAnimalClick = (animal: AnimalInfo, currentViewType: currentViewTypes) => {
    if (currentViewType !== currentViewTypes.basic) {
      return; 
    }
    if (onAnimalClick) onAnimalClick(animal.animal_id);
  }

  // #region | Button List
  const ButtonRow = memo(({ currentViewType, listType }: { currentViewType: currentViewTypes, listType: ListTypes }) => {
    const [contentsRes, icons, onclicks] = useContents(
      () => { dispatch({ currentViewType: currentViewTypes.add }) },
      () => { dispatch({ currentViewType: currentViewTypes.move }) },
      () => { dispatch({ currentViewType: currentViewTypes.edit }); feedSkipSuccess.current = {}; },
      () => {
        _state.tempData = [];
        setFeedSkipTime(new Map());
        dispatch({ currentViewType: currentViewTypes.basic })
      },
      async () => {
        handleSave();
        const groupSkips = new Map<number, number[]>();

        feedSkipTime.entries().forEach(([locationId, time]) => {
          const timeLocations = groupSkips.get(time);
          if (timeLocations) {
            groupSkips.set(time, [...timeLocations, locationId]);
            return;
          } 
          groupSkips.set(time, [locationId]);
        })
       
        setLoading(true);
        for (const [timeOffsetInHours, locationIds] of Array.from(groupSkips.entries())) {
          const time = Math.floor(Date.now()/1000) + 60*60*timeOffsetInHours;
          const skippedResult = await skipFeedTimeLocations({domain: _state.connection.connection.domain, skipFeedTimeLocations: {time, locationIds} } )
          skippedResult.forEach((result) => feedSkipSuccess.current[result.id] = result.success);
        } 
        setLoading(false);
        setFeedSkipTime(new Map());
      },
      currentViewType,
      listType
    );

    return (
      <NewButtonList
        contents={contentsRes}
        onClick={(i) => { onclicks[i]?.() }}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={icons}
        disabled={currentViewType !== currentViewTypes.basic ? [false, false, true, true, true] : [false, false, false]}
        overrides={(index) => {
          const { backgroundColor, opacity } = getButtonStyles(index, _state.currentViewType, listType);
          return {
            contentStyles: { color: colors.greyDarkPrimary },
            hoverStyles: { backgroundColor: colors.greyLightSecondary },
            baseStyles: { backgroundColor: backgroundColor },
            disabledStyles: { opacity: opacity }
          }
        }}
      />
    );
  });
  // #endregion
  
  // #region | Pen List
  const PensList_View = useMemo(() => {
    let State_Data = [..._state.pens];

    if (headerPen) {
      const headerPenLocation: Location = {
        location_id: -1, 
        name: "headerPen",
        animals: [],
      };
      State_Data.unshift(headerPenLocation);
    }

    if (_state.currentViewType === currentViewTypes.add) {
      _state.tempData.forEach(e => {
        let index = State_Data.findIndex(pen => pen.location_id === e.data.location);

        if (index >= 0) {
          let updatedPen = { ...State_Data[index] };
          let animals = updatedPen.animals ? [...updatedPen.animals] : [];
          let newAnimal = { animal_id: e.animalId, location_id: e.data.location, ...e.data } as AnimalInfo
          delete newAnimal.location_id;
          if (!animals.find(a => a.animal_id === newAnimal.animal_id)) {
            animals.push(newAnimal);
          }
          
          updatedPen.animals = animals;
          State_Data[index] = updatedPen;
        }
      });
    }
    
    return State_Data
      .toSorted((a, b) => (a.location_id > b.location_id) ? 1 : -1)
      .map((pen, pIndex) => {

        if (headerPen && pen.location_id === -1) {
          return (
            <Header currentViewType={_state.currentViewType}/>
          );
        }

        let penName = pen.name;
        let penSkip = feedSkipSuccess.current[pen.location_id] !== undefined ? (
          <div 
            key={'res_' + pen.location_id} 
            style={{ gridArea: "1/2/2/3", height: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
            {feedSkipSuccess.current[pen.location_id] ? 
              <glyphs.Check fillColor={colors.systemGreen} size={"15px"} /> 
              : 
              <glyphs.Empty fillColor={colors.systemRed} size={"15px"} />
            }
          </div>
        ) : "";
        let isFirstPen = pIndex === 0;
        let isLastPen = pIndex === State_Data.length - 1;
        let emptyPen = pen.animals.length === 0

        let animalsToRender;
        if (_state.currentViewType === currentViewTypes.add) {
          animalsToRender = pen.animals.filter(animal => {
            const tempEntry = _state.tempData.find(t => t.animalId === animal.animal_id);
        
            if (tempEntry && tempEntry.data.location !== pen.location_id) {
              return false;
            }
            return true;
          });
        } else {
          animalsToRender = pen.animals;
        }
        
        let animals = emptyPen
          ? <div style={{ ...css.emptyPen, cursor: "default", opacity: emptyPen ? "0.5" : "1" }}>{t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty Pen"})}</div>
          : animalsToRender.map((animal, aIndex) => {
            const isFirstAnimal = aIndex === 0;
        
        if (_state.currentViewType === currentViewTypes.move && _state.tempData.find((e) => e.animalId === animal.animal_id && Number.isNaN(e.data.location))) return <></>;
            
            return (
              <div key={animal.animal_id} style={{ position: "relative"}} /* onClick={() => handleOnAnimalClick(animal, _state.currentViewType)}*/>
                <div style={{position: "absolute", left: "-30px", transform: "translateY(-50%)", top: "50%"}}>
                  {(_state.currentViewType === currentViewTypes.add && _state.tempData.find((e) => e.animalId === animal.animal_id)) || _state.currentViewType === currentViewTypes.move || (_state.currentViewType === currentViewTypes.basic && onAnimalClick !== undefined)
                    ? (
                    <NewButton
                      content=""
                      onClick={() =>
                        _state.currentViewType === currentViewTypes.add
                          ? handleAddDelete(animal.animal_id, pen.location_id)
                        : _state.currentViewType === currentViewTypes.move ?
                            handleMoveDelete(animal.animal_id, pen.location_id) :
                            handleOnAnimalClick(animal, _state.currentViewType)
                      }
                      buttonType={ButtonTypes.textButton}
                      startEnhancer={_state.currentViewType === currentViewTypes.basic ? glyphs.ArrowSmooth({fillColor: colors.greyDarkSecondary}) : glyphs.Delete({ fillColor: colors.greyDarkSecondary })}
                      overrides={{
                        startEnhancerStyles: { margin: "0px 0px 2px 0px" }
                      }}
                    />
                    ) : <></>}
                </div>
                <ANIMAL defaultCursor={onPenClick === undefined} penIndex={pen.location_id} animalIndex={animal.animal_id} />
                {isFirstAnimal ? <></> : <div style={{ position: "absolute", border: "1px solid " + colors.greyTertiary, top: "0px", left: "-10px", right: "-10px" }} />}
            </div>
          )
        });
        
        return (
          <frames.PenItem onClick={() => handleOnPenClick(pen, _state.currentViewType)} $isHovered={onPenClick !== undefined && _state.currentViewType === currentViewTypes.basic&&pen.animals.length !== 0 ? true : false} key={pen.location_id} style={{borderRadius: isFirstPen ? "10px 10px 0px 0px" : isLastPen ? "0px 0px 10px 10px" : "0px"}}>
          <div style={{ height: "35px", display: "flex", alignItems: "center" }}>
            <div style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, pointerEvents: "none"}}>{penName}</div>
          </div>
          <div style={{ height: "35px", display: "flex", alignItems: "center", paddingRight: _state.currentViewType === currentViewTypes.edit ? "15px" : "0px" }}>
            {_state.currentViewType === currentViewTypes.edit ?
              <NewInput
                key={`input${pen.location_id}`}
                placeholder={t("units.hours", { defaultValue: "hours" })}
                onChange={(e) => setFeedSkipTime(old => {old.set(pen.location_id, Number(e)); return old})}
                fieldOverrides={{
                  fieldWidth: "100%",
                  fieldStyle: NewFieldStyles.greyTertiary,
                  fieldType: NewFieldTypes.tertiaryField
                }}
                valueOverrides={{ valueType: "number"}}
              />
              : <div>{penSkip}</div>}
          </div>
          <div style={{ height: "fit-content", display: "flex", flexDirection: "column", justifyContent: "center", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
            {_state.currentViewType === currentViewTypes.add || _state.currentViewType === currentViewTypes.move ? <EmptyContent pen={pen} currentViewType={_state.currentViewType} error={_state.error} handleAddAnimal={handleAddAnimal}/> : <></>}
              <div style={{ minHeight: "35px", display: "flex", flexDirection: "column", justifyContent: "center"}}>
                {animals}
              </div>
            </div>
        </frames.PenItem>
      )
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_state.pens, _state.currentViewType, _state.tempData, _state.error, loading, missUseOfMemoFixer])
  // #endregion

  return (
    <PenContext.Provider value={{ state: _state, dispatch }}>
      {_state.currentViewType !== currentViewTypes.basic ? <Overlay overrides={{ color: colors.transparent, genericStyle: {bottom: "calc(100% - 170px)"} }} beforeUnload onClickAlert/> : <></>}
      <div style={{zIndex: "999", position: "relative"}}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", width: "735px", padding: "0px 20px", boxSizing: "border-box" }}>
        <p style={{ ...css.penHeaders, paddingLeft: "6px" }}>{useLevelDisclaimer(_state.currentViewType)}</p>
          <ButtonRow currentViewType={_state.currentViewType} listType={listType}/>
        </div>
        <div style={{display: "flex", flexDirection: "column", justifyContent: "center", gap: "10px", width: "735px"}}>
          {PensList_View}
          {loading && <WaitAnimation/>}
        </div>
      </div>
    </PenContext.Provider>
  );
}